/* eslint-disable max-lines */
import { useState, useMemo } from 'react';
import toast from 'react-hot-toast';
import Button from '@components/atoms/ui-kit/button.tsx';
import Modal from '@components/atoms/modals/modal.tsx';
import { MultiValue } from 'react-select';
import { useTranslateDocument } from '@utils/api/documents/use-translate-document.tsx';
import { useTranslation } from 'react-i18next';
import Input from '@components/atoms/ui-kit/input.tsx';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';
import { css, StyleSheet } from 'aphrodite';
import cn from 'classnames';
import { DocumentTranslationLanguage, Option } from '@utils/types/shared.ts';
import { useSharedTranscript } from '@utils/providers/shared-transcript-context.tsx';
import { useSharedDocumentFinished } from '@utils/api/magic-links/use-finish-shared-document.tsx';
import { AxiosError } from 'axios';
import { BaseError } from '@utils/types/shared/base.ts';
import { CheckCircle2, Download, Languages } from 'lucide-react';
import ExportDocumentForm from '@components/workspace/export-document-form.tsx';
import SelectInput from '@components/atoms/ui-kit/select-input.tsx';
import {
  getDocumentDirectUrl,
  triggerDownloadFromUrl
} from '@utils/api/documents/use-get-document-direct.tsx';
import { getDocumentRaw } from '@utils/api/documents/use-get-document-raw.tsx';
import { parse } from '@utils/extensions/content-disposition.extension.ts';
import saveAs from 'file-saver';

export const SharedTranscriptExportDocument = () => {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();

  const { documentResult, session, token } = useSharedTranscript();
  const sharedDocumentFinishedMutation = useSharedDocumentFinished();

  const [modal, setModal] = useState<'' | 'translate' | 'docx' | 'template'>(
    ''
  );
  const [exportModal, setExportModal] = useState(false);
  const [includeSpeakers, setIncludeSpeakers] = useState(true);
  const [includeTimestamps, setIncludeTimestamps] = useState(true);

  const translateDocumentMutation = useTranslateDocument();
  const [selectedLanguages, setSelectedLanguages] = useState<Option[]>([]);
  const [filenameInput, setFilenameInput] = useState<string>(
    session?.filename || ''
  );

  const backendReady = session?.allow_magic_link_export !== undefined;
  const allowExport = session?.allow_magic_link_export ?? false;

  const styles = useMemo(
    () =>
      StyleSheet.create({
        topNavContainer: {
          borderBottom: `1px solid ${getColour('border')}`
        },
        topNavContainerColor: {
          backgroundColor: getColour('secondLayer'),
          border: `1px solid ${getColour('border')}`
        },
        iconsColor: {
          color: getColour('icon')
        },
        userText: {
          color: getColour('normalText')
        }
      }),
    [getColour]
  );

  const options: Option[] = Object.keys(DocumentTranslationLanguage).map(
    key =>
      ({
        label: key,
        value:
          DocumentTranslationLanguage[
            key as keyof typeof DocumentTranslationLanguage
          ]
      }) as Option
  );

  const handleMediaDownload = () => {
    if (!allowExport) {
      toast.error(
        t('export-not-allowed-organization') ||
          'Export functionality is not enabled for this document'
      );
      return;
    }

    if (!session?.media_url) {
      toast.error(t('no-media-available'));
      return;
    }

    const a = document.createElement('a');
    a.href = session.media_url;
    a.setAttribute('download', session.filename || 'media-file');
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleTranslate = async () => {
    try {
      if (!allowExport) {
        toast.error(
          t('export-not-allowed-organization') ||
            'Export functionality is not enabled for this document'
        );
        return;
      }

      if (!selectedLanguages || selectedLanguages.length === 0) {
        toast.error(t('select-at-least-one-language'));
        return;
      }

      toast(t('translating-document'));

      const response = await translateDocumentMutation.mutateAsync({
        sessionId: session?.session_id as string,
        documentId: documentResult?.result.id as string,
        languages: selectedLanguages.map(lang => lang.value.toUpperCase()),
        filename: filenameInput
      });

      setModal('');

      const shouldAutoDownload =
        (documentResult?.result.use_plain_document &&
          !documentResult?.result.plain_document_changed) ||
        false;

      if (
        shouldAutoDownload &&
        response?.json &&
        Array.isArray(response.json)
      ) {
        for (const doc of response.json) {
          if (!doc.id || !session?.session_id) continue;
          try {
            const resp = await getDocumentDirectUrl({
              sessionId: session.session_id,
              documentId: doc.id,
              format: 'docx'
            });
            await triggerDownloadFromUrl(
              resp.result.download_url,
              resp.result.filename,
              resp.result.content_type
            );
          } catch {
            try {
              const resp = await getDocumentRaw({
                sessionId: session.session_id,
                documentId: doc.id,
                format: 'docx'
              });
              const disposition = parse(
                resp.headers['content-disposition'] || ''
              );
              const fname =
                disposition?.parameters?.filename ||
                doc.filename ||
                'translated.docx';
              saveAs(resp.data, fname);
            } catch {
              toast.error(t('error-downloading-document'));
            }
          }
        }
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      if (
        error?.response?.status === 400 &&
        error?.response?.data?.error === 'export-not-allowed'
      ) {
        toast.error(
          error.response.data.error_description ||
            t('export-not-allowed-organization') ||
            'Export functionality is not enabled for shared documents in this organization'
        );
      } else {
        const data = error?.response?.data;
        let errorMessage = t('error-translating-document');

        if (data?.error_description) {
          errorMessage = data.error_description;
        } else if (data?.detail) {
          if (Array.isArray(data.detail)) {
            errorMessage = data.detail
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              .map((err: any) => {
                const field = err.loc?.[1] ?? 'field';
                return `${field}: ${err.msg}`;
              })
              .join('\n');
          } else if (typeof data.detail === 'string') {
            errorMessage = data.detail;
          }
        } else if (data?.error) {
          errorMessage = data.error;
        } else if (data?.message) {
          errorMessage = data.message;
        }

        toast.error(errorMessage);
      }
    }
  };

  const handleFinishDocument = async () => {
    try {
      await sharedDocumentFinishedMutation.mutateAsync({
        sessionId: session?.session_id as string,
        documentId: documentResult?.result.id as string
      });
      toast.success(t('document-finished-notification'));
    } catch (error) {
      const err = error as AxiosError<BaseError>;
      const data = err.response?.data;
      const fallbackMessage = t('error-finishing-document');

      let errorMessage = fallbackMessage;

      if (data?.error_description) {
        errorMessage = data.error_description;
      } else if (data?.detail) {
        if (Array.isArray(data.detail)) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          errorMessage = (data.detail as any[])
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .map((e: any) => e.msg)
            .join(', ');
        } else if (typeof data.detail === 'string') {
          errorMessage = data.detail;
        }
      } else if (data?.error) {
        errorMessage = data.error;
      } else if (data?.message) {
        errorMessage = data.message;
      }

      toast.error(errorMessage);
    }
  };

  const getDisabledTooltip = () => {
    if (!backendReady) {
      return t('export-feature-coming-soon') || 'Export feature coming soon';
    }
    if (!allowExport) {
      return (
        t('export-disabled-by-organization') ||
        'Export is disabled by the organization administrator'
      );
    }
    return undefined;
  };

  return (
    <>
      <div
        className={cn(
          'flex gap-2 content-center items-center',
          css(styles.userText)
        )}
      >
        <div className="flex items-center gap-1 flex-wrap">
          <Button
            size="sm"
            variant="secondary"
            icon={<Languages size={12} />}
            label={t('translate')}
            onClick={() => setModal('translate')}
            disabled={!allowExport}
            title={getDisabledTooltip()}
            className="h-9 max-w-fit min-[1080px]:w-32 justify-center text-xs px-2!"
          />
          <Button
            size="sm"
            variant="secondary"
            icon={<Download size={12} />}
            label={t('export')}
            onClick={() => setExportModal(true)}
            disabled={!allowExport}
            title={getDisabledTooltip()}
            className="h-9 max-w-fit min-[1080px]:w-32 justify-center text-xs px-2!"
          />
          <Button
            size="sm"
            variant="secondary"
            icon={<CheckCircle2 size={12} />}
            label={t('finish')}
            onClick={handleFinishDocument}
            className="h-9 max-w-fit min-[1080px]:w-32 justify-center text-xs px-2!"
          />
        </div>

        {session?.session_id && documentResult?.result.id && (
          <ExportDocumentForm
            modalTranscript={exportModal}
            setModalTranscript={setExportModal}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            selectedItem={session as any}
            handleMediaDownload={handleMediaDownload}
            includeTimestamps={includeTimestamps}
            setIncludeTimestamps={setIncludeTimestamps}
            includeSpeakers={includeSpeakers}
            setIncludeSpeakers={setIncludeSpeakers}
            sessionId={session.session_id}
            documentId={documentResult.result.id}
            isShared={true}
            token={token}
          />
        )}

        <Modal
          isOpen={modal === 'translate'}
          onClose={() => setModal('')}
          size="medium"
          title={t('translate-document') + ' ' + (session?.filename || '')}
          caption={t('select-languages-to-translate')}
          footerActions={[
            <Button
              onClick={() => setModal('')}
              label={t('cancel')}
              size="small"
              variant="smoke"
              key="cancel-translate"
            />,
            <Button
              onClick={handleTranslate}
              label={t('translate')}
              size="small"
              variant="primary"
              loading={translateDocumentMutation.isLoading}
              disabled={translateDocumentMutation.isLoading}
              key="confirm-translate"
            />
          ]}
        >
          <div className="mb-4 flex gap-2 items-center text-sm">
            <label
              htmlFor="translationInput"
              className={cn(
                'block text-gray-700 col-span-2 min-w-32',
                css(styles.userText)
              )}
            >
              {t('translation-name')}
            </label>
            <Input
              id="translationInput"
              placeholder={session?.filename}
              value={filenameInput}
              onChange={e => setFilenameInput(e.target.value)}
              className={cn('col-span-3')}
            />
          </div>

          <div className="mb-4 flex gap-2 items-center text-sm">
            <label
              htmlFor="languageSelect"
              className={cn(
                'block text-gray-700 max-w-fit col-span-2 min-w-32',
                css(styles.userText)
              )}
            >
              {t('translate-to')}
            </label>
            <SelectInput
              id="languageSelect"
              value={selectedLanguages}
              name="type"
              onChange={(selectedOptions: MultiValue<Option>) => {
                setSelectedLanguages(selectedOptions as Option[]);
              }}
              className="col-span-3 w-full"
              options={options}
              isMulti
            />
          </div>
        </Modal>
      </div>
    </>
  );
};
